/**
 * Helpers for the Ingenious AI web messenger (the "Chat with ISC" widget).
 *
 * ISC's loader snippet sits in `public/index.html`, exactly as it does on their other properties,
 * so `window.ingeniousai` exists before React mounts. These helpers plus `@components/Chatbot`
 * only cover what a snippet on a static page cannot: SPA route changes, signed-in identity, and
 * discarding the conversation on logout.
 */

import { IUser } from '@typings';
import { AccountTypeEnum } from '@utils/enums';

/**
 * The subset of the messenger API eNVD uses. Note that before `init()` resolves, `window.ingeniousai`
 * is only the loader stub (`init`/`on`/`render`/`destroy`) - the remaining methods are added once the
 * vendor host script has booted, so always read `window.ingeniousai` fresh rather than caching it.
 */
export interface IIngeniousMessenger {
    init(options: Record<string, unknown>): Promise<void>;
    on(event: string, handler: () => void): void;
    createConversation(options: { displayName: string; description?: string }): Promise<unknown>;
    getConversations(): unknown[];
    updateUser(options: { metadata: Record<string, unknown> }): Promise<unknown>;
    /**
     * Optional because these are absent from the loader stub and only appear once the vendor host
     * script has booted - always call them with `?.()`.
     */
    close?(): void;
    destroy?(): void;
    /** Resets the vendor's anonymous user, discarding the conversation. */
    logout?(): void;
}

declare global {
    interface Window {
        /**
         * @description The Ingenious AI web messenger. Undefined until `public/ingenious-loader.js` has run.
         */
        ingeniousai?: IIngeniousMessenger;
    }
}

/** ISC's Ingenious integration. Shared with the other ISC properties (myMLA, LPA, NLIS). */
export const INGENIOUS_INTEGRATION_ID = '66cbfa05c375bb31ae69c963';

/**
 * Routes the launcher must not appear on. Signed-out pages only - there is no account context for
 * a conversation there, and the chip would sit over the login and registration calls to action.
 */
export const CHATBOT_EXCLUDED_PATHS = ['/', '/callback', '/logout'];

const MESSENGER_CONTAINER_ID = 'web-messenger-container';
const MESSENGER_BUTTON_ID = 'messenger-button';
const MESSENGER_STYLE_ID = 'envd-messenger-branding';
const TRAILING_SLASH = /\/$/;

/**
 * `init()` may only be called once per page load, and the widget lives outside the React tree, so
 * the guard has to be module state rather than component state. Held here (not in the component) so
 * the logout path can reset it after tearing the messenger down.
 */
let isMessengerBootstrapped = false;

export const hasMessengerBootstrapped = (): boolean => isMessengerBootstrapped;

export const markMessengerBootstrapped = (): void => {
    isMessengerBootstrapped = true;
};

export const resetMessengerBootstrap = (): void => {
    isMessengerBootstrapped = false;
};

export const getSanitizedUrl = (url: string): string => url.trim().replace(TRAILING_SLASH, '');

/**
 * Identity handed to the bot so it can recognise the customer instead of asking them to log in to
 * myMLA again. Mirrors the fields the myMLA snippet sends (`token`, `pic`, `userId`), with the
 * multi-PIC additions eNVD needs.
 *
 * Sunshine metadata is a flat map of scalars - nested objects and arrays are not supported - so the
 * PIC list travels as a comma separated string alongside the singular `pic` the bot already reads.
 */
export const buildChatUserMetadata = (
    user: IUser | null | undefined,
    currentUrl: string
): Record<string, string | number> => {
    const metadata: Record<string, string | number> = { currentUrl };

    if (!user) {
        return metadata;
    }

    if (user.token) {
        metadata.token = user.token;
    }
    if (user.claims?.auth0Id) {
        metadata.userId = user.claims.auth0Id;
    }

    // The account the customer is currently working as.
    if (user.accountDetails?.pic) {
        metadata.pic = user.accountDetails.pic;
    }
    if (user.accountDetails?.entityName) {
        metadata.entityName = user.accountDetails.entityName;
    }

    // Every PIC they hold. A PIC is an LPA account's label - see mapToAccountDetails.
    const pics = (user.claims?.accounts ?? [])
        .filter((account) => account?.accountType === AccountTypeEnum.LPA && !!account.accountLabel)
        .map((account) => account.accountLabel);
    const uniquePics = Array.from(new Set(pics));

    if (uniquePics.length > 0) {
        metadata.pics = uniquePics.join(',');
        metadata.picCount = uniquePics.length;
    }

    return metadata;
};

export const isChatbotHiddenPath = (pathname: string): boolean =>
    CHATBOT_EXCLUDED_PATHS.includes(getSanitizedUrl(pathname) || '/');

/**
 * Shows or hides the whole widget. The launcher and conversation live in an iframe the vendor
 * appends to the body, so visibility is toggled on that element rather than through React.
 */
export const setMessengerVisibility = (visible: boolean): void => {
    const container = document.getElementById(MESSENGER_CONTAINER_ID);
    if (!container) {
        return;
    }

    if (!visible) {
        // Collapse an open conversation first, so re-showing does not reveal it mid-conversation.
        window.ingeniousai?.close?.();
    }
    container.style.display = visible ? '' : 'none';
};

/**
 * Ends the conversation and forgets it, so the next person to use this browser starts clean.
 *
 * The messenger keeps its anonymous user id and message history in `localStorage`, which survives
 * the Auth0 logout redirect. `logout()` resets the vendor's user, `destroy()` removes the widget,
 * and the key sweep covers whatever the vendor left behind. Scoped to the vendor's own keys - the
 * app's other `localStorage` entries are left alone.
 */
export const clearMessengerSession = (integrationId: string = INGENIOUS_INTEGRATION_ID): void => {
    try {
        window.ingeniousai?.logout?.();
    } catch {
        // Best effort - the sweep below is what actually guarantees the history is gone.
    }

    try {
        window.ingeniousai?.destroy?.();
    } catch {
        // As above.
    }

    try {
        const vendorKey = new RegExp(`smooch|ingenious|${integrationId}`, 'i');
        Object.keys(window.localStorage)
            .filter((key) => vendorKey.test(key))
            .forEach((key) => window.localStorage.removeItem(key));
    } catch {
        // localStorage can throw in private-browsing modes; nothing more we can do.
    }

    resetMessengerBootstrap();
};

const getMessengerDocument = (): Document | null => {
    const container = document.getElementById(MESSENGER_CONTAINER_ID) as HTMLIFrameElement | null;
    return container?.contentDocument ?? null;
};

export interface IMessengerBranding {
    buttonLabel: string;
    buttonIconUrl: string;
    appIconUrl: string;
    brandColor: string;
    hoverColor: string;
}

/**
 * Escapes a value for safe interpolation into a CSS string or url() token.
 */
const escapeForCss = (value: string): string => value.replace(/\\/g, '\\\\').replace(/["'\n\r]/g, '\\$&');

/**
 * Restyles the launcher button inside the messenger iframe. The vendor widget renders into an iframe,
 * so this cannot be done with app CSS - the styles have to be written into the iframe document.
 *
 * Deliberately CSS-only. The launcher and the in-conversation icon are rendered by the vendor's own
 * React inside that iframe, so replacing their nodes (or their attributes) makes React lose track of
 * what it mounted: the next commit calls removeChild on a node that is no longer there and throws
 * NotFoundError, which unmounts the whole widget. Pseudo-elements and background images never appear
 * in React's tree, so it has nothing to reconcile and nothing to crash on.
 */
export const applyMessengerBranding = (doc: Document, branding: IMessengerBranding): void => {
    if (doc.getElementById(MESSENGER_STYLE_ID)) {
        return;
    }

    const style = doc.createElement('style');
    style.id = MESSENGER_STYLE_ID;
    style.textContent = `
        #${MESSENGER_BUTTON_ID} {
            /* !important throughout: the vendor sets width, height and backgroundColor as inline
               styles on this button, and inline styles otherwise win. */
            background-color: ${escapeForCss(branding.brandColor)} !important;
            border-radius: 40px !important;
            display: flex !important;
            align-items: center !important;
            justify-content: center !important;
            gap: 10px !important;
            color: white !important;
            font-size: 1rem !important;
        }
        /* The vendor's own launcher content is hidden rather than removed - see the note above. */
        #${MESSENGER_BUTTON_ID} > * {
            display: none !important;
        }
        /* The icon is a fixed-size flex item rather than a background on the button: the source
           image is 1600x1600, so it must be given explicit dimensions to scale down to. Sized to
           match buttonHeight in the Chatbot component, which is what the previous markup-based
           branding achieved with height: 100% on an <img>. Kept as explicit px rather than a
           percentage or aspect-ratio so the box can never collapse to zero and lose the icon. */
        #${MESSENGER_BUTTON_ID}::before {
            content: '' !important;
            display: block !important;
            flex: 0 0 auto !important;
            width: 46px !important;
            height: 46px !important;
            background-image: url("${escapeForCss(branding.buttonIconUrl)}") !important;
            background-repeat: no-repeat !important;
            background-position: center !important;
            background-size: contain !important;
        }
        #${MESSENGER_BUTTON_ID}::after {
            content: "${escapeForCss(branding.buttonLabel)}" !important;
            white-space: nowrap !important;
        }
        .app-icon {
            content: url("${escapeForCss(branding.appIconUrl)}");
        }
        .carousel-title {
            white-space: normal;
        }
        a.btn-primary:hover {
            background-color: ${escapeForCss(branding.hoverColor)} !important;
            border-color: ${escapeForCss(branding.hoverColor)} !important;
        }
    `;
    doc.body.appendChild(style);
};

/**
 * The messenger iframe is rendered asynchronously after `init()` resolves, so poll briefly for it
 * rather than assuming it is already in the DOM.
 */
export const waitForMessengerDocument = (timeoutMs = 10000, intervalMs = 200): Promise<Document | null> =>
    new Promise((resolve) => {
        const started = Date.now();

        const check = () => {
            const doc = getMessengerDocument();
            if (doc?.getElementById(MESSENGER_BUTTON_ID)) {
                resolve(doc);
                return;
            }
            if (Date.now() - started >= timeoutMs) {
                resolve(null);
                return;
            }
            window.setTimeout(check, intervalMs);
        };

        check();
    });
